import { getCustomers } from 'client/actions/customer'
import { navigateAddModal, navigateRemoveModal } from 'client/actions/navigation'
import { getAllProducts } from 'client/actions/products'
import { importCustomers } from 'client/api/customer'
import { importProducts } from 'client/api/productsV4'
import Button, { ButtonType } from 'client/components/common/Button/Button'
import LoadingEllipsis from 'client/components/common/LoadingEllipsis'
import { ImportTypes } from 'client/components/Screen/ImportScreen/ImportScreen'
import MappingHeader from 'client/components/Screen/ImportScreen/MappingView/MappingHeader/MappingHeader'
import MappingRows from 'client/components/Screen/ImportScreen/MappingView/MappingRows/MappingRows'
import {
  checkRequiredFields,
  generateCustomerPayload,
  generateProductPayload,
  getAutomapping,
  isCustomerPayload,
  isProductPayload,
  parseFileToArray,
  processImport,
  sleep,
} from 'client/components/Screen/ImportScreen/MappingView/MappingViewHelper'
import { openImportScreen } from 'client/components/Screen/ScreenActions'
import { Analytics } from 'client/Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from 'client/Global/AnalyticsEvents'
import { CustomerInterface } from 'client/Interfaces/CustomerInterface'
import { ProductForImportInterface, ProductTypesEnum } from 'client/Interfaces/ProductInterface'
import { l } from 'client/Other/Translation'
import { companySelector } from 'client/reducers/company'
import { settingsSelector } from 'client/reducers/settings'
import _ from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import './MappingView.scss'

interface MappingViewProps {
  file: File
  type: ImportTypes
  close(): void
}

export interface Mapping {
  [field: string]: number // row number
}

interface ParsingError {
  message: string
}

const MappingView: React.FC<MappingViewProps> = ({ file, type, close }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [skip, setSkip] = React.useState<number[]>([]) // col number
  const [mapping, setMapping] = React.useState<Mapping>({})
  const [fileContent, setFileContent] = React.useState<string[][]>([])
  const [errors, setErrors] = React.useState<ParsingError | null>()

  const settings = useSelector(settingsSelector)
  const invoiceSettings = settings.models.find((model) => model.type == 'invoice')
  const userCountry = useSelector(companySelector)?.country

  const dispatch = useDispatch()

  const importStart = async () => {
    const errorFields = checkRequiredFields(type, mapping)

    if (errorFields.length === 0) {
      let payload: (CustomerInterface | ProductForImportInterface)[] = []

      const fileContentFiltred = fileContent.filter((el, i) => !skip.includes(i))

      if (type === 'CUSTOMER') {
        const language = _.find(invoiceSettings?.fields, { name: 'language' })?.default || ''
        const paymentTime = _.find(invoiceSettings?.fields, { name: 'paymentTime' })?.default || ''

        payload = generateCustomerPayload(fileContentFiltred, mapping, String(language), paymentTime, userCountry)
      }
      if (type === 'PRODUCT') {
        let productType = ProductTypesEnum.goods

        const invoiceModel = settings.models.find((m) => m.type === 'invoice')

        if (invoiceModel && invoiceModel.fields && Array.isArray(invoiceModel.fields)) {
          const productTypeSetting = invoiceModel.fields.find((field) => field.name === 'productType')

          productType = ((productTypeSetting && productTypeSetting.default?.toString()) ||
            ProductTypesEnum.goods) as ProductTypesEnum
        }

        payload = generateProductPayload(fileContentFiltred, mapping, productType)
      }

      const importFunc = async (
        chunk: (CustomerInterface | ProductForImportInterface)[],
        current: number,
        total: number,
      ) => {
        dispatch(
          navigateAddModal({
            component: 'LoadingModal',
            props: {
              header: l('import.importing.header'),
              text: l('Import.Importing', '', { '1': String(current), '2': String(total) }),
            },
          }),
        )

        await sleep(_.random(100, 500))

        if (isCustomerPayload(chunk)) {
          await importCustomers(chunk)
        }
        if (isProductPayload(chunk)) {
          await importProducts(chunk)
        }
        dispatch(navigateRemoveModal('LoadingModal'))
      }

      const results = await processImport(payload, 100, importFunc)

      if (results.fulfilled > 0) {
        if (type === 'CUSTOMER') {
          Analytics.trackEvent(Events.importCustomers, {
            type: AnalyticsType.action,
            category: Category.customer,
            placement: Placement.customers,
            result: Result.success,
            step: 2,
          })
        }
        if (type === 'PRODUCT') {
          Analytics.trackEvent(Events.importProducts, {
            type: AnalyticsType.action,
            category: Category.product,
            placement: Placement.products,
            result: Result.success,
            step: 2,
          })
        }
      }

      if (type === 'CUSTOMER') {
        dispatch(getCustomers())
      }
      if (type === 'PRODUCT') {
        dispatch(getAllProducts())
      }

      close()

      dispatch(
        navigateAddModal({
          component: 'ConfirmationModal',
          props: {
            localizationKeys: {
              header: 'Import.Done',
              confirmationButtonLabel: 'OK',
            },
            modalBody: (
              <>
                {results.fulfilled > 0 && (
                  <p>{l(`Import.${type.toLowerCase()}.imported`, '', { n: String(results.fulfilled) })}</p>
                )}
                {results.rejected > 0 && (
                  <p>{l(`Import.${type.toLowerCase()}.NotImported`, '', { '1': String(results.rejected) })}</p>
                )}
              </>
            ),
            onConfirm: () => {
              dispatch(navigateRemoveModal('ConfirmationModal'))
            },
            onClose: () => {
              dispatch(navigateRemoveModal('ConfirmationModal'))
            },
          },
        }),
      )
    } else {
      const errorFieldsText = errorFields.map((key) => l(`Import.${key}`)).join(', ')
      const errorMessage = l('Import.Required') + ' ' + errorFieldsText

      dispatch(
        navigateAddModal({
          component: 'ConfirmationModal',
          props: {
            localizationKeys: {
              header: 'Alert',
              confirmationButtonLabel: 'OK',
            },
            modalBody: <p>{errorMessage}</p>,
            onConfirm: () => {
              dispatch(navigateRemoveModal('ConfirmationModal'))
            },
            onClose: () => {
              dispatch(navigateRemoveModal('ConfirmationModal'))
            },
          },
        }),
      )

      if (type === 'CUSTOMER') {
        Analytics.trackEvent(Events.importCustomers, {
          type: AnalyticsType.action,
          category: Category.customer,
          placement: Placement.customers,
          result: Result.error,
          errorReason: errorMessage,
          step: 2,
        })
      }
      if (type === 'PRODUCT') {
        Analytics.trackEvent(Events.importProducts, {
          type: AnalyticsType.action,
          category: Category.product,
          placement: Placement.products,
          result: Result.error,
          errorReason: errorMessage,
          step: 2,
        })
      }
    }
  }

  // Parse file
  React.useEffect(() => {
    const getFileContent = async (file: File) => {
      try {
        const content = await parseFileToArray(file)
        setIsLoading(false)
        setFileContent(content)
      } catch (e) {
        console.error(e)
        const error = {
          message: l('import.error.parsing'),
        }
        setErrors(error)
        setIsLoading(false)

        if (type === 'CUSTOMER') {
          Analytics.trackEvent(Events.importCustomers, {
            type: AnalyticsType.action,
            category: Category.customer,
            placement: Placement.customers,
            result: Result.error,
            errorReason: error.message,
            step: 2,
          })
        }
        if (type === 'PRODUCT') {
          Analytics.trackEvent(Events.importProducts, {
            type: AnalyticsType.action,
            category: Category.product,
            placement: Placement.products,
            result: Result.error,
            errorReason: error.message,
            step: 2,
          })
        }
      }
    }

    getFileContent(file)
  }, [file])

  // Automapping fields
  React.useEffect(() => {
    if (type === 'CUSTOMER') {
      const newMapping = getAutomapping(fileContent)
      setMapping(newMapping)
    }
  }, [fileContent])

  if (isLoading) {
    return <LoadingEllipsis />
  }

  return (
    <div className="mapping-view">
      <h2>{l('import.mapping.title')}</h2>
      <h5>{l('import.mapping.desc')}</h5>
      <div className="mapping-view__wrapper">
        {fileContent.length > 0 && (
          <div
            className="mapping-view__grid"
            style={{ gridTemplateColumns: `30px repeat(${fileContent[0].length}, minmax(180px, 1fr))` }}
          >
            <MappingHeader
              type={type}
              fileContent={fileContent}
              mapping={mapping}
              setMapping={setMapping}
              skip={skip}
              setSkip={setSkip}
            />
            <MappingRows fileContent={fileContent} skip={skip} setSkip={setSkip} />
          </div>
        )}
        {errors && <div className="mapping-view__errors">{errors.message}</div>}
      </div>

      <div className="Buttons">
        {!errors && (
          <Button
            label={l('import.Do')}
            type={ButtonType.accept}
            action={async () => await importStart()}
            disabled={skip.length === fileContent.length}
          />
        )}
        <Button
          label={l('Back')}
          type={ButtonType.cancel}
          action={() => {
            if (type === 'CUSTOMER') {
              Analytics.trackEvent(Events.importCustomers, {
                type: AnalyticsType.action,
                category: Category.customer,
                placement: Placement.customers,
                result: Result.cancel,
                step: 2,
              })
            }
            if (type === 'PRODUCT') {
              Analytics.trackEvent(Events.importProducts, {
                type: AnalyticsType.action,
                category: Category.product,
                placement: Placement.products,
                result: Result.cancel,
                step: 2,
              })
            }

            dispatch(openImportScreen(type, 'Picker'))
          }}
        />
      </div>
    </div>
  )
}

export default MappingView
